import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './addressinputblock.module.css';
import { useTranslation } from "react-i18next";
import useSWR from 'swr';
import LoadingScreen from '../LoadingScreen';
import { get } from '../../apis';
import { toast } from 'react-toastify';

const AddressInputBlock = ({ onSave, onCancel, initialAddress }) => {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [plusAddress, setPlusAddress] = useState('');

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedWard, setSelectedWard] = useState('');

  const [selectedCityName, setSelectedCityName] = useState('');
  const [selectedDistrictName, setSelectedDistrictName] = useState('');
  const [selectedWardName, setSelectedWardName] = useState('');

  const [phoneError, setPhoneError] = useState('');
  const [formError, setFormError] = useState('');
  
  const { t } = useTranslation();

  const { data, error, isLoading } = useSWR("https://raw.githubusercontent.com/ductridev/DiaGioiHanhChinhVN/master/data.json", get, {
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (retryCount >= 10) return;
      setTimeout(() => revalidate({ retryCount }), 10000);
    }
  });

  useEffect(() => {
    if (initialAddress) {
      if (initialAddress.fullName) setFullName(initialAddress.fullName);
      if (initialAddress.phone) setPhone(initialAddress.phone);
      if (initialAddress.address) setAddress(initialAddress.address);
      if (initialAddress.plusAddress) setPlusAddress(initialAddress.plusAddress);
    }
    if (!isLoading && !error) setCities(data);
  }, [data, isLoading, error, initialAddress]);

  useEffect(() => {
    if (error && !isLoading) toast(t('cannot_fetch_address_data'));
  }, [error, isLoading, t]);

  useEffect(() => {
    if (cities && initialAddress && initialAddress.selectedCity) {
      setSelectedCity(initialAddress.selectedCity);
      setSelectedCityName(initialAddress.selectedCityName);
    }
  }, [cities, initialAddress]);

  useEffect(() => {
    if (selectedCity && cities) {
      const city = cities.find((city) => city.Id == selectedCity);
      if (city) {
        setDistricts(city.Districts);
      }
    } else {
      setDistricts([]);
      setWards([]);
    }
  }, [selectedCity, cities]);

  useEffect(() => {
    if (districts && initialAddress && initialAddress.selectedDistrict) {
      setSelectedDistrict(initialAddress.selectedDistrict);
      setSelectedDistrictName(initialAddress.selectedDistrictName);
    }
  }, [districts, initialAddress]);

  useEffect(() => {
    if (selectedDistrict) {
      const district = districts.find((district) => district.Id == selectedDistrict);
      if (district) {
        setWards(district.Wards);
      }
    } else {
      setWards([]);
    }
  }, [selectedDistrict, districts]);

  useEffect(() => {
    if (wards && initialAddress && initialAddress.selectedWard) {
      setSelectedWard(initialAddress.selectedWard);
      setSelectedWardName(initialAddress.selectedWardName);
    }
  }, [wards, initialAddress]);

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    const cityName = event.target.options[event.target.selectedIndex].text;
    setSelectedCity(cityId);
    setSelectedCityName(cityName);
    setSelectedDistrict('');
    setSelectedWard('');
    setDistricts([]);
    setWards([]);
  };

  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    const districtName = event.target.options[event.target.selectedIndex].text;
    setSelectedDistrict(districtId);
    setSelectedDistrictName(districtName);
    setSelectedWard('');
    setWards([]);
  };

  const handleWardChange = (event) => {
    const wardName = event.target.options[event.target.selectedIndex].text;
    setSelectedWard(event.target.value);
    setSelectedWardName(wardName);
  };

  const validatePhoneNumber = (phone) => {
    const phonePattern = /^[0-9]{10,11}$/; // Adjust the pattern as per your phone number format
    return phonePattern.test(phone);
  };

  const handleSave = () => {
    // Reset errors
    setPhoneError('');
    setFormError('');

    // Validate form
    if (!fullName || !phone || !selectedCity || !selectedDistrict || !selectedWard || !plusAddress) {
      setFormError(t("fill_all_data"));
      return;
    }

    if (!validatePhoneNumber(phone)) {
      setPhoneError(t("invalid_phone"));
      return;
    }

    onSave({
      fullName,
      phone,
      address: `${selectedWardName}, ${selectedDistrictName}, ${selectedCityName}`,
      plusAddress,
      selectedCity,
      selectedDistrict,
      selectedWard,
      selectedCityName,
      selectedDistrictName,
      selectedWardName
    });
  };

  return (
    isLoading ? <LoadingScreen />
      : (
        error ? null
          :
          <div className={styles['block-container']}>
            {formError && <div className="alert alert-danger">{formError}</div>}
            {phoneError && <div className="alert alert-danger">{phoneError}</div>}
            <div className="row align-items-center mb-3">
              <label htmlFor="fullName" className="col-sm-2 col-form-label">{t("full_name")}</label>
              <div className="col-sm-10">
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder={t("full_name")}
                  className="form-control"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
            </div>
            <div className="row align-items-center mb-3">
              <label htmlFor="phone" className="col-sm-2 col-form-label">{t("phone_number")}</label>
              <div className="col-sm-10">
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  placeholder={t("enter_phone_number")}
                  className="form-control"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="mb-3 row">
              <label htmlFor="city" className="col-sm-2 col-form-label">{t("address")}</label>
              <div className="col-sm-10 d-flex">
                <div className="col-sm-4 me-2">
                  <select
                    className="form-select"
                    id="city"
                    aria-label={t("select_city")}
                    value={selectedCity}
                    onChange={handleCityChange}
                  >
                    <option value="">{t("city")}</option>
                    {cities.map((city) => (
                      <option key={city.Id} value={city.Id}>{city.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-4 me-2">
                  <select
                    className="form-select"
                    id="district"
                    aria-label={t("select_district")}
                    value={selectedDistrict}
                    onChange={handleDistrictChange}
                    disabled={!selectedCity}
                  >
                    <option value="">{t("district")}</option>
                    {districts.map((district) => (
                      <option key={district.Id} value={district.Id}>{district.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-4">
                  <select
                    className="form-select"
                    id="ward"
                    aria-label={t("select_ward")}
                    value={selectedWard}
                    onChange={handleWardChange}
                    disabled={!selectedDistrict}
                  >
                    <option value="">{t("ward")}</option>
                    {wards.map((ward) => (
                      <option key={ward.Id} value={ward.Id}>{ward.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="row align-items-center mb-3">
              <label htmlFor="plusAddress" className="col-sm-2 col-form-label">{t("detailed_address")}</label>
              <div className="col-sm-10">
                <input
                  type="text"
                  id="plusAddress"
                  name="plusAddress"
                  placeholder={t("enter_detailed_address")}
                  className="form-control"
                  value={plusAddress}
                  onChange={(e) => setPlusAddress(e.target.value)}
                />
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <button type="button" className="btn btn-secondary" onClick={onCancel}>{t("cancel")}</button>
              <button type="button" className="btn btn-success" onClick={handleSave}>{t("save")}</button>
            </div>
          </div>
      )
  );
};

export default AddressInputBlock;
